import { useState, useEffect } from 'react';
import { Receipt, Download, Loader2, AlertTriangle, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMyInvoices, downloadMyInvoicePdf } from '@/services/apiClient';
import { toast } from 'sonner';

/**
 * A company's own invoices.
 *
 * Exists because the lapsed-subscription banner tells people to clear their
 * balance, and until now there was nowhere for them to see what that balance
 * was, when it was due, or where to pay it. Being asked to settle a bill you
 * cannot look at is not a payment prompt, it is a dead end.
 */

const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const STATUS_TONE = {
    issued:         'bg-muted text-muted-foreground border-border',
    sent:           'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
    partially_paid: 'bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/30',
    paid:           'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/30',
    overdue:        'bg-destructive/10 text-destructive border-destructive/30',
    cancelled:      'bg-muted text-muted-foreground border-border line-through',
    void:           'bg-muted text-muted-foreground border-border line-through',
};

export default function MyInvoicesPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMyInvoices()
            .then(res => setData(res.data))
            .catch(() => setData({ invoices: [], totalOutstanding: 0 }))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex h-28 items-center justify-center rounded-xl border border-border bg-card">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
        );
    }

    const invoices = data?.invoices || [];
    if (invoices.length === 0) return null;

    const outstanding = data?.totalOutstanding || 0;
    // Payment details are frozen onto each invoice at issue, so the newest
    // open one carries the instructions that currently apply.
    const payTo = invoices.find(i => i.amountDue > 0)?.paymentInstructions
        || invoices[0]?.paymentInstructions
        || {};
    const hasPayTo = payTo.bankAccountNumber || payTo.upiId;

    return (
        <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-semibold text-foreground">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                    Invoices
                </h2>
                {outstanding > 0 && (
                    <span className="flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1 text-sm font-medium text-destructive">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {money(outstanding)} outstanding
                    </span>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left">
                            <th className="pb-2 font-medium text-muted-foreground">Invoice</th>
                            <th className="pb-2 font-medium text-muted-foreground">Issued</th>
                            <th className="pb-2 font-medium text-muted-foreground">Due</th>
                            <th className="pb-2 text-right font-medium text-muted-foreground">Total</th>
                            <th className="pb-2 text-right font-medium text-muted-foreground">Outstanding</th>
                            <th className="pb-2 font-medium text-muted-foreground">Status</th>
                            <th className="pb-2" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {invoices.map(inv => (
                            <tr key={inv._id}>
                                <td className="py-2.5 font-mono text-xs">{inv.invoiceNumber}</td>
                                <td className="py-2.5 text-muted-foreground">{fmtDate(inv.issueDate)}</td>
                                <td className={`py-2.5 ${inv.status === 'overdue' ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
                                    {fmtDate(inv.dueDate)}
                                </td>
                                <td className="py-2.5 text-right tabular-nums">{money(inv.total)}</td>
                                <td className="py-2.5 text-right font-medium tabular-nums">
                                    {inv.amountDue > 0 ? money(inv.amountDue) : '—'}
                                </td>
                                <td className="py-2.5">
                                    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[inv.status] || ''}`}>
                                        {inv.status.replace('_', ' ')}
                                    </span>
                                </td>
                                <td className="py-2.5 text-right">
                                    <Button
                                        size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                                        onClick={() => downloadMyInvoicePdf(inv._id, inv.invoiceNumber)
                                            .catch(e => toast.error(e.message))}
                                    >
                                        <Download className="h-3 w-3" /> PDF
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {outstanding > 0 && hasPayTo && (
                <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4">
                    <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Landmark className="h-3.5 w-3.5 text-muted-foreground" /> Where to pay
                    </h3>
                    <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                        {payTo.bankAccountName && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">Account name</dt>
                                <dd className="font-medium">{payTo.bankAccountName}</dd>
                            </div>
                        )}
                        {payTo.bankName && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">Bank</dt>
                                <dd className="font-medium">{payTo.bankName}</dd>
                            </div>
                        )}
                        {payTo.bankAccountNumber && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">Account number</dt>
                                <dd className="font-mono text-xs font-medium">{payTo.bankAccountNumber}</dd>
                            </div>
                        )}
                        {payTo.bankIfsc && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">IFSC</dt>
                                <dd className="font-mono text-xs font-medium">{payTo.bankIfsc}</dd>
                            </div>
                        )}
                        {payTo.upiId && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">UPI</dt>
                                <dd className="font-mono text-xs font-medium">{payTo.upiId}</dd>
                            </div>
                        )}
                    </dl>
                    <p className="mt-2.5 text-[11px] text-muted-foreground">
                        Payments are reconciled by hand. Once it clears, your account manager
                        records it and full access returns straight away.
                    </p>
                </div>
            )}
        </div>
    );
}
